import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'wasp/client/auth';
import { isAdministrator } from '../../client/lib/platformRole';
import { createLocation, getAllLocations, getLocationTypes, getLocationBySlug } from 'wasp/client/operations';
import { useQuery } from 'wasp/client/operations';
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary
} from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { AlertCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { CANADIAN_PROVINCE_OPTIONS, COUNTRY_CANADA } from './canadianProvinces';

const CreateLocationPage = () => {
  // Utility to generate slug from location name
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[\s.-]/g, '')
      .trim();
  };
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ submit?: string }>({});
  const [isSlugEdited, setIsSlugEdited] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    province: '' as string,
    country: COUNTRY_CANADA,
    locationTypeId: '' as string | null,
    isPublic: false,
    allowPublicSubscription: true,
    defaultVisibility: 'VERIFIED',
    publicDescription: '',
    timezone: 'UTC',
    // Hub fields
    isHub: false,
    aggregateChildContent: true,
    parentLocationId: '' as string | null
  });

  // Fetch location types from database
  const { data: locationTypes = [] } = useQuery(getLocationTypes);

  // Fetch available parent locations (exclude hubs and self)
  const { data: allLocations } = useQuery(getAllLocations, { limit: 100 });
  const locationsArray = (allLocations && typeof allLocations === 'object' && 'locations' in allLocations && Array.isArray((allLocations as any).locations))
    ? (allLocations as any).locations
    : [];
  const availableParents = locationsArray.filter((loc: any) => !loc.isHub);

  const validateForm = (): boolean => {
    const newErrors: { submit?: string } = {};
    if (!formData.name.trim()) {
      newErrors.submit = 'Location name is required';
    }
    if (!formData.province) {
      newErrors.submit = 'Province is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const slug = formData.slug || generateSlug(formData.name);

    // Check uniqueness of slug
    try {
      const existing = await getLocationBySlug({ slug });
      if (existing && (existing as any).id) {
        setErrors({ submit: 'A location with this slug already exists. Please choose a different name.' });
        return;
      }
    } catch (err) {
      console.error('Error checking slug uniqueness:', err);
    }

    setIsLoading(true);
    setErrors({});

    try {
      const locationData = {
        ...formData,
        locationCode: slug,
      };

      await createLocation(locationData);
      toast({
        title: "Success",
        description: "Location created successfully"
      });
      navigate('/admin/taxonomy/location-names');
    } catch (error) {
      console.error('Error creating location:', error);
      const errorMessage = (error as Error).message || 'Failed to create location. Please try again.';
      setErrors({ submit: errorMessage });
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear submit error when user starts typing
    if (errors.submit) {
      setErrors({});
    }
  };

  if (!user || !isAdministrator(user)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">Admin privileges required.</p>
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="location-form"
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Location'}
          </ActionButton>
          <ActionButtonSecondary
            onClick={() => navigate('/admin/taxonomy/location-names')}
          >
            Cancel
          </ActionButtonSecondary>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Guidelines">
        <div className="space-y-3 text-sm text-gray-600">
          <p>
            <span className="font-medium text-gray-900">Name:</span> A unique identifier for this location
          </p>
          <p>
            <span className="font-medium text-gray-900">Type:</span> Categorize the location (optional)
          </p>
          <p>
            <span className="font-medium text-gray-900">Province:</span> Select a Canadian province for this location
          </p>
          <p>
            <span className="font-medium text-gray-900">Visibility:</span> Controls who can see content by default
          </p>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      sidebarContent={sidebarContent}
      layoutVariant="taxonomy"
      authUser={user}
    >
      <div className="max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New Location</h1>
          <p className="text-gray-600">Add a new location to manage community content and settings</p>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">{errors.submit}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} id="location-form" className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Location Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const newName = e.target.value;
                    setFormData(prev => ({ ...prev, name: newName }));
                    if (!isSlugEdited) {
                      setFormData(prev => ({ ...prev, slug: generateSlug(newName) }));
                    }
                    if (errors.submit) setErrors({});
                  }}
                  placeholder="Enter location name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="slug">Slug (Location Code) *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => {
                    setIsSlugEdited(true);
                    setFormData(prev => ({ ...prev, slug: generateSlug(e.target.value) }));
                    if (errors.submit) setErrors({});
                  }}
                  placeholder="e.g. toronto-hub"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">This will be used in URLs and cannot be changed later.</p>
              </div>

              <div>
                <Label htmlFor="province">Province *</Label>
                <Select
                  value={formData.province || 'none'}
                  onValueChange={(value) => handleInputChange('province', value === 'none' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select province" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select province</SelectItem>
                    {CANADIAN_PROVINCE_OPTIONS.map((province) => (
                      <SelectItem key={province.value} value={province.value}>{province.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value="Canada"
                  disabled
                  readOnly
                />
              </div>
              
              <div>
                <Label htmlFor="locationTypeId">Location Type</Label>
                <Select
                  value={formData.locationTypeId || 'none'}
                  onValueChange={(value) => handleInputChange('locationTypeId', value === 'none' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {(locationTypes as any[]).filter((lt: any) => lt.isActive).map((lt: any) => (
                      <SelectItem key={lt.id} value={lt.id}>{lt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="publicDescription">Public Description</Label>
                <Textarea
                  id="publicDescription"
                  value={formData.publicDescription}
                  onChange={(e) => handleInputChange('publicDescription', e.target.value)}
                  placeholder="Describe this location for public visitors"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Location Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Location Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="isPublic">Public Location</Label>
                  <p className="text-sm text-gray-600">Allow public visitors to view this location</p>
                </div>
                <Switch
                  id="isPublic"
                  checked={formData.isPublic}
                  onCheckedChange={(checked) => handleInputChange('isPublic', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="allowPublicSubscription">Allow Public Subscription</Label>
                  <p className="text-sm text-gray-600">Allow users to subscribe without invitation</p>
                </div>
                <Switch
                  id="allowPublicSubscription"
                  checked={formData.allowPublicSubscription}
                  onCheckedChange={(checked) => handleInputChange('allowPublicSubscription', checked)}
                />
              </div>
              
              <div>
                <Label htmlFor="defaultVisibility">Default Content Visibility</Label>
                <Select value={formData.defaultVisibility} onValueChange={(value) => handleInputChange('defaultVisibility', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PUBLIC">Public</SelectItem>
                    <SelectItem value="SUBSCRIBER">Subscribers Only</SelectItem>
                    <SelectItem value="VERIFIED">Verified Members Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Timezone */}
          <Card>
            <CardHeader>
              <CardTitle>Timezone</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={formData.timezone} onValueChange={(value) => handleInputChange('timezone', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Europe/Paris">Paris</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                    <SelectItem value="Asia/Shanghai">Shanghai</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Hub/Hierarchy Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Hub & Hierarchy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="isHub">Is Hub Location</Label>
                  <p className="text-sm text-gray-600">Can this location contain other locations?</p>
                </div>
                <Switch
                  id="isHub"
                  checked={formData.isHub}
                  onCheckedChange={(checked) => handleInputChange('isHub', checked)}
                />
              </div>

              {formData.isHub && (
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="aggregateChildContent">Aggregate Child Content</Label>
                    <p className="text-sm text-gray-600">Include content from child locations</p>
                  </div>
                  <Switch
                    id="aggregateChildContent"
                    checked={formData.aggregateChildContent}
                    onCheckedChange={(checked) => handleInputChange('aggregateChildContent', checked)}
                  />
                </div>
              )}

              {!formData.isHub && (
                <div>
                  <Label htmlFor="parentLocationId">Parent Location</Label>
                  <Select value={formData.parentLocationId || 'none'} onValueChange={(value) => handleInputChange('parentLocationId', value === 'none' ? null : value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {availableParents.map((loc: any) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        </form>
      </div>
    </IndividualEditPageLayout>
  );
};

export default CreateLocationPage;



